import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'sonner';
import BottomNav from './components/BottomNav';
import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuthCallback from './pages/AuthCallback';
import OnboardingPage from './pages/OnboardingPage';
import Dashboard from './pages/Dashboard';
import LiraHome from './pages/LiraHome';
import SessionEvolution from './pages/SessionEvolution';
import ChatRoom from './pages/ChatRoom';
import ScenariosPage from './pages/ScenariosPage';
import ScenarioLesson from './pages/ScenarioLesson';
import ConversationsPage from './pages/ConversationsPage';
import SettingsPage from './pages/SettingsPage';
import SavedWordsPage from './pages/SavedWordsPage';
import FlashcardsPage from './pages/FlashcardsPage';
import ListeningPracticePage from './pages/ListeningPracticePage';
import LearningPathsPage from './pages/LearningPathsPage';
import LearningPathDetailPage from './pages/LearningPathDetailPage';
import LessonPage from './pages/LessonPage';
import GuidedLessonPage from './pages/GuidedLessonPage';
import ProgressPage from './pages/ProgressPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import ChatPage from './pages/ChatPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import VoiceDiagnosticsPage from './pages/VoiceDiagnosticsPage';
import { startSession, endSession, trackEvent, EVENTS } from './services/analyticsService';
import './App.css';

// Analytics tracker component
const AnalyticsTracker = () => {
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      startSession(location.pathname);
    }
    return () => {
      if (user) {
        endSession();
      }
    };
  }, [user]);

  useEffect(() => {
    if (user) {
      trackEvent(EVENTS.PAGE_VIEW, { page: location.pathname });
    }
  }, [location.pathname, user]);

  return null;
};

// Layout with Bottom Navigation
const AppLayout = ({ children, showNav = true }) => {
  return (
    <div className={showNav ? 'pb-16' : ''}>
      {children}
      {showNav && <BottomNav />}
    </div>
  );
};

const ProtectedRoute = ({ children, showNav = true }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="animate-pulse text-dark-muted text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to onboarding if not completed
  if (!user.onboarding_completed && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <AppLayout showNav={showNav}>
      {children}
    </AppLayout>
  );
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="animate-pulse text-dark-muted text-lg">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Router component that handles OAuth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id - handles OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      
      {/* Password Reset Routes - Public */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      
      {/* OAuth Callback */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected Routes */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute showNav={false}>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/home"
        element={
          <ProtectedRoute showNav={false}>
            <LiraHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:conversationId"
        element={
          <ProtectedRoute showNav={false}>
            <ChatRoom />
          </ProtectedRoute>
        }
      />
      <Route
        path="/session/:conversationId/complete"
        element={
          <ProtectedRoute showNav={false}>
            <SessionEvolution />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scenarios"
        element={
          <ProtectedRoute>
            <ScenariosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/guided-lesson/:scenarioId"
        element={
          <ProtectedRoute showNav={false}>
            <GuidedLessonPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversations"
        element={
          <ProtectedRoute>
            <ConversationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lesson/:scenarioId"
        element={
          <ProtectedRoute showNav={false}>
            <ScenarioLesson />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/saved-words"
        element={
          <ProtectedRoute>
            <SavedWordsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/flashcards"
        element={
          <ProtectedRoute>
            <FlashcardsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/listening"
        element={
          <ProtectedRoute showNav={false}>
            <ListeningPracticePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning"
        element={
          <ProtectedRoute>
            <LearningPathsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning/:pathId"
        element={
          <ProtectedRoute>
            <LearningPathDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning/:pathId/:moduleId/:lessonId"
        element={
          <ProtectedRoute showNav={false}>
            <LessonPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/progress"
        element={
          <ProtectedRoute showNav={false}>
            <ProgressPage />
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes - Protected by role check inside component */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute showNav={false}>
            <AnalyticsDashboard />
          </ProtectedRoute>
        }
      />
      {/* Voice Diagnostics - SPRINT 3A Testing */}
      <Route
        path="/diagnostics/voice"
        element={
          <ProtectedRoute showNav={false}>
            <VoiceDiagnosticsPage />
          </ProtectedRoute>
        }
      />
      {/* Legacy route redirect */}
      <Route
        path="/analytics"
        element={<Navigate to="/admin/dashboard" replace />}
      />

      {/* Public Legal Pages - accessible without login */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <div className="App">
              <AppRouter />
              <Toaster position="top-center" richColors />
              <AnalyticsTracker />
            </div>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;